import WorkbenchWCanvasSlotActionsGenerator, {
  SET_TARGET_DEFINITION_DIRTY,
  SET_DATA_FROM_TARGET,
  FETCH_DATA_FROM_TARGET_FOR_GRAPHS,
  UPDATE_STREAMING_OPTION,
  SET_TARGET_ENVIRONMENT,
  SET_TARGET_DEFINITION,
  SUBMIT_TO_DC,
} from '@/views/workbenchCanvas/store/WorkbenchWCanvasSlotActionsGenerator'
import ConfigAPI from '@/api/ConfigAPI'
import definitionSerializer from '@/appComponents/oldWorkbench/targetDefinition/services/serializer'
import transformAttributes from '@/components/common/attributes'
import { cloneDeep } from 'lodash'

import { fetchAdTargetData, fetchSingleTarget, fetchEsDateRanges, submitToDC } from '@/api/index'

import {
  SET_DATA_SOURCES,
  SET_DAY_PARTS,
  SET_AUDIENCE_SIZE,
  SET_HISTORICAL_VIEWERSHIP,
  SET_UNIVERSE,
} from './mutations'

import { SET_METRICS, SET_LOADING, SET_QUICK_ADD, SET_FETCHING_TARGET } from '@/views/workbenchCanvas/store/mutations'
import {
  SET_DEFINITION,
  SET_TO_VIEW_ONLY,
  REMOVE_VIEW_ONLY,
} from '@/appComponents/oldWorkbench/targetDefinition/store/actions'
import {
  INIT_DATE_RANGE,
  UPDATE_START_DATE,
  UPDATE_END_DATE,
} from '@/appComponents/oldWorkbench/dateRange/store/actions'
import { actions as undoActions } from '@/store/plugins/undoHistory'
import { SET_CURRENT_TARGET, SET_SELECTED_VIEWERSHIP_NETWORKS } from '@/appLogic/targets/currentTarget/store/actions'
import {
  GET_REVISION_HISTORY,
  FORWARD_REVISION_HISTORY,
  SET_TARGET_BRANCH,
  SET_PRIMARY_BRANCH,
  SET_MISSING_BRANCH,
} from '@/appLogic/revisionHistory/store/actions'
import generateLabel from '@/appLogic/targetDefinition/LabelMoveGenerator'
import Bluebird from 'bluebird'
import { INTENTS, ACTIVATION_STATUS } from '@/appLogic/targets/Constants'
import Toast from '@/components/common/toasts'
const adConfigApi = ConfigAPI.getConfigFilesForConfigType('ad-config.json')

class WorkbenchActions extends WorkbenchWCanvasSlotActionsGenerator {
  initActionBody({ dispatch, commit, getters, state }, id) {
    commit(SET_LOADING, true)
    let manuallySetDateRange = false
    Bluebird.props({
      revisionHistory: dispatch(GET_REVISION_HISTORY, { targetId: id }),
      config: adConfigApi.fetchConfig(),
      // TODO need to adjust for full target
      target: fetchSingleTarget({ target_id: id }).then(resp => resp.data),
    })
      .then(async data => {
        const { config, target } = data

        const esDateRanges = await fetchEsDateRanges()

        commit(SET_CURRENT_TARGET, target)
        // have to set the config datasets based off the users permissions and the targets intents
        let intents = target.metadata.intents
        let hasMarketingIntent = intents.filter(x => x.IntentId === INTENTS.Marketing.IntentId).length > 0
        let hasResearchIntent = intents.filter(x => x.IntentId === INTENTS.Research.IntentId).length > 0
        if (hasMarketingIntent) {
          config.datasets = config.datasets.filter(x => x.id === 'marketing')
        } else if (hasResearchIntent) {
          config.datasets = config.datasets.filter(x => x.id === 'household' || x.id === 'respondent')
        } else {
          config.datasets = config.datasets.filter(x => x.id === 'programs')
          dispatch(SET_TO_VIEW_ONLY)
        }

        dispatch(SET_TARGET_BRANCH, { name: target.display_name })
        if (!getters.revisionHistoryCurrentBranch) {
          dispatch(SET_MISSING_BRANCH, { id: target.id, display_name: target.display_name })
        } else {
          dispatch(SET_PRIMARY_BRANCH, { name: target.display_name })
        }
        if (getters.isTargetSubmitted || getters.isTargetActivated || getters.isTargetArchived) {
          dispatch(SET_TO_VIEW_ONLY)
        } else {
          dispatch(REMOVE_VIEW_ONLY)
        }
        return adConfigApi.fetchDatasets(config.datasets, target.target_datasource).then(datasets => {
          return { datasets, config, esDateRanges }
        })
      })
      .then(({ datasets, config, esDateRanges }) => {
        commit(
          SET_DATA_SOURCES,
          datasets.map(dataset => Object.freeze(transformAttributes(dataset, dataset.id))),
        )
        if (!getters.currentTarget.metadata.intents.map(i => i.IntentId).includes(INTENTS.Marketing.IntentId)) {
          commit(SET_QUICK_ADD, config.quick_add)
        }
        commit(SET_DAY_PARTS, config.day_parts)
        dispatch(INIT_DATE_RANGE, esDateRanges)
        // set data range...use targets date range if it is valid otherwise keep default
        if (getters.isValidStartDate(getters.currentTarget.target_definition.startDate)) {
          dispatch(UPDATE_START_DATE, getters.currentTarget.target_definition.startDate)
          dispatch(UPDATE_END_DATE, getters.currentTarget.target_definition.endDate)
        } else {
          manuallySetDateRange = true
        }
        if (getters.currentTarget.target_definition.hasOwnProperty('viewSource')) {
          dispatch(UPDATE_STREAMING_OPTION, getters.currentTarget.target_definition.viewSource)
        }
        commit(SET_DEFINITION, definitionSerializer.deserialize(getters.currentTarget.target_definition.query, getters))
        // By default if a user has not clarified what networks
        // he/she would like to see we default to the turner networks
        if (getters.isTargetActivated || getters.isTargetSubmitted) {
          let currentTarget = getters.currentTarget
          dispatch(SET_DATA_FROM_TARGET, {
            metrics: currentTarget.metrics || currentTarget.workbench_data.metrics,
            audienceSize: currentTarget.audience_size || currentTarget.workbench_data.audience_size,
            historicalViewership:
              currentTarget.viewership_historical || currentTarget.workbench_data.viewership_historical,
            universe: currentTarget.universe || currentTarget.workbench_data.universe,
          })
          commit(SET_LOADING, false)
        } else if (getters.isTargetEmpty) {
          commit(SET_LOADING, false)
        } else {
          return dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS).then(() => {
            dispatch(undoActions.INITIALIZE, getters.getDefinition)
          })
        }
      })
      .then(() => dispatch(SET_TARGET_DEFINITION_DIRTY, manuallySetDateRange))
  }

  targetDataToSave({ commit, state, getters }) {
    const rhpm = getters.revisionHistoryPrimaryMove
    if (rhpm) {
      // TODO Revision History should include the Networks
      const td = rhpm.target_data
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_datasource: getters.currentTarget.target_datasource,
        target_definition: rhpm.target_definition,
        audience_size: td.audienceSize,
        universe: td.universe,
        metrics: td.metrics,
        viewership_historical: td.historicalViewership,
        networks: getters.selectedViewershipNetworks,
      }
    } else {
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_datasource: getters.currentTarget.target_datasource,
        target_definition: getters.targetDefinition,
        audience_size: getters.audienceSize,
        universe: getters.universe,
        metrics: state.metrics,
        viewership_historical: getters.historicalViewership,
        networks: getters.selectedViewershipNetworks, // NETWORKS
      }
    }
  }

  fetchDataFromTargetForGraphs({ dispatch, commit, state, getters }) {
    if (getters.isFetchingTarget || getters.isTargetEmpty) return
    commit(SET_LOADING, true)
    commit(SET_FETCHING_TARGET, true)
    const targetDefinitionToFetch = getters.targetDefinition
    return fetchAdTargetData(targetDefinitionToFetch).then(resp => {
      if (targetDefinitionToFetch !== getters.targetDefinition) {
        // target def has changed since the last query happened...fetch latest data
        commit(SET_FETCHING_TARGET, false)
        dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS)
        return null
      } else {
        const targetData = {
          metrics: resp.metrics,
          audienceSize: resp.audienceSize && resp.audienceSize.audience,
          historicalViewership: resp.historicalViewership,
          universe: resp.universe,
        }
        dispatch(SET_DATA_FROM_TARGET, targetData)
        const lastMove = getters.targetDefinitionLastAttributeCommand
        const userHasMadeAForwardAction = lastMove
        if (userHasMadeAForwardAction) {
          // TODO add the label based off the lastMove
          dispatch(FORWARD_REVISION_HISTORY, {
            targetData,
            targetDefinition: targetDefinitionToFetch,
            audience: targetData.audienceSize,
            label: generateLabel(lastMove),
          })
        }
        commit(SET_LOADING, false)
        commit(SET_FETCHING_TARGET, false)
      }
    })
  }

  dataFromTargetToSet(commit, data) {
    const { metrics, audienceSize, historicalViewership, universe } = data
    commit(SET_METRICS, metrics)
    commit(SET_AUDIENCE_SIZE, audienceSize)
    commit(SET_HISTORICAL_VIEWERSHIP, historicalViewership)
    commit(SET_UNIVERSE, universe)
  }

  additionalActions() {
    return {
      [SET_TARGET_ENVIRONMENT]: (context, targetData) => {
        context.dispatch(SET_FETCHING_TARGET, true)
        context.dispatch(SET_TARGET_DEFINITION, targetData.targetDefinition)
        context.dispatch(SET_DATA_FROM_TARGET, targetData.targetData)
        // we have to set a timeout because of the watcher
        setTimeout(() => {
          context.dispatch(SET_FETCHING_TARGET, false)
        }, 50)
      },
      [SET_TARGET_DEFINITION]: (context, targetDefinition) => {
        context.dispatch(UPDATE_START_DATE, targetDefinition.startDate)
        context.dispatch(UPDATE_END_DATE, targetDefinition.endDate)
        context.dispatch(SET_DEFINITION, definitionSerializer.deserialize(targetDefinition.query, context.getters))
        context.commit(SET_SELECTED_VIEWERSHIP_NETWORKS, targetDefinition.networks)
        context.dispatch(UPDATE_STREAMING_OPTION, targetDefinition.viewSource)
      },
      [SUBMIT_TO_DC]({ dispatch, commit, state, getters }) {
        let target = cloneDeep(getters.currentTarget)
        target.activation_status = ACTIVATION_STATUS.SUBMITTED
        target.activated = true
        submitToDC(target)
          .then(res => {
            Toast.success({
              title: 'Successfully Sent Downstream',
            })
            commit(SET_CURRENT_TARGET, target)
          })
          .catch(async resp => {
            Toast.error({
              title: 'Error sending target Downstream',
            })
          })
      },
    }
  }
}

export default WorkbenchActions



// WEBPACK FOOTER //
// ./src/app/views/workbench/AD/store/actions.js